<template>
  <section class="csvActionsModal">
    <vyModal
      :class="{'upload-download-modal': true, 'setup': showSetupSteps}"
      v-if="showUploadDownloadModal && !showUploadStarted"
      header-title="CSV Download/Upload"
      @close="$emit('close')"
      @cancel="$emit('close')"
    >
      <div slot="body">
        <div v-show="showSetupSteps">
          <startingGuide :current-step="datasetName" @download-clicked="downloadTemplate" @upload-clicked="upload"></startingGuide>
        </div>
        <div v-show="!showSetupSteps" class="upload-download-body">
          <section class="download">
            <header>
              <h1>Download</h1>
              <span class="download-icon"><v-icon>far fa-arrow-down</v-icon></span>
            </header>
            <hr class="divider" />
            <h3>Existing Data</h3>
            <p>Download your existing {{datasetName.charAt(0).toUpperCase() + datasetName.slice(1)}} dataset to add or edit manually.</p>
            <v-btn
              @click="downloadTemplate"
              depressed
              color="#4D52E5"
              class="upload-button"
            >Download</v-btn>
          </section>
          <section class="upload">
            <header>
              <h1>Upload</h1>
              <span class="upload-icon"><v-icon>far fa-arrow-up</v-icon></span>
            </header>
            <hr class="divider" />
            <h3>Ready to upload</h3>
            <p>Save the file after editing with "{{ datasetName }}" as the file name. Make sure the file is in CSV format with UTF-8 encoding.</p>
            <v-btn
              @click="upload"
              depressed
              color="#4D52E5"
              class="upload-button"
            >Upload</v-btn>
            <p class="centered">Upload edits or appends entries and will not remove any data.</p>
          </section>
        </div>
      </div>
      <div slot="footer" class="help-copy mt">
        <strong>Have questions?</strong>
        <br>
        <span>Check out our <a :href="brandingService.getSupportUrl('csvImports')" target="_blank">knowledge base article</a> containing the details, examples and FAQs about importing and updating data.</span>
      </div>
    </vyModal>

    <vyModal
      class="download-modal"
      v-if="showDownloadModal"
      header-title="CSV Download/Upload"
      @cancel="showDownloadModal = false"
    >
      <div slot="body" class="px-5">
        <div class="modal-header mb-5 text-xs-center">
          <v-icon color="#4CAF50" size=20>fa fa-check</v-icon>
          <span class="ml-2">{{datasetName.charAt(0).toUpperCase() + datasetName.slice(1)}} template downloaded!</span>
        </div>
        <v-layout column align-center>
            <buildings-guidelines v-if="datasetName === 'buildings'" />
            <networks-guidelines v-if="datasetName === 'networks'" />
            <spaces-guidelines v-if="datasetName === 'spaces'" />
            <sensors-guidelines v-if="datasetName === 'sensors'" />
        </v-layout>
      </div>
      <div slot="footer" class="help-copy mt">
        <strong>Have questions?</strong>
        <br>
        <span>Check out our <a :href="brandingService.getSupportUrl('csvImports')" target="_blank">knowledge base article</a> containing the details, examples and FAQs about importing and updating data.</span>
      </div>
    </vyModal>

    <vyModal
      class="upload-modal"
      v-if="showUploadModal"
      header-title="CSV Download/Upload"
      @close="showUploadModal = false"
      @cancel="showUploadModal = false"
    >
      <div slot="body">
        <div v-show="showUploadStarted">
          <div class="modal-subheader mb-4">
            <v-icon>fa fa-spinner-third fa-spin light-gray</v-icon>
            <span>Uploading...</span>
          </div>
          <div class="mb-3">
            An email will be sent (including any errors) when processing is complete. <span class="font-italic">If you need to upload again, wait until you receive your email notification.</span>
          </div>
        </div>

        <div v-show="showUploadSuccessful">
          <div class="modal-subheader mb-4">
            <v-icon class="check-icon">fas fa-check</v-icon>
            <span>Uploaded</span>
          </div>
          <div class="mb-5">
            <ul v-if="uploadResponse">
              <li>{{ uploadResponse.added }} were successfully added</li>
              <li>{{ uploadResponse.updated }} were successfully updated</li>
              <li>{{ uploadResponse.errored }} were not added or updated due to errors
                  <ul>
                    <li v-for="(error, index) in uploadResponse.errors" :key="index">
                      <span v-if="error.id === 'row'">
                        Error: {{ error.id }} {{ error.lineNumber }} - {{ error.message }}
                      </span>
                      <span v-if="error.id !== 'row'">
                        Error: {{ error.id }} - {{ error.message }}
                      </span>
                    </li>
                  </ul>
              </li>
            </ul>
          </div>
          <v-btn color="primary" @click="showSetupSteps = false; showUploadModal = false">{{datasetName.charAt(0).toUpperCase() + datasetName.slice(1)}}</v-btn>
        </div>

        <div v-show="showCSVError">
          <div class="modal-subheader mb-4">
            <v-icon class="warning-icon">fas fa-exclamation-triangle</v-icon>
            <span>Upload unsuccessful</span>
          </div>
          <div>{{ uploadError }}</div>
        </div>

        <div v-show="showCollectorError">
          <div class="modal-subheader mb-4">
            <v-icon class="warning-icon">fas fa-exclamation-triangle</v-icon>
            <span>Upload unsuccessful</span>
          </div>
          <div>The collector is currently updating this dataset. Please wait to upload manual edits until the collector upload is finished.</div>
        </div>
      </div>

      <div slot="footer" class="help-copy">
        <div v-show="showCSVError || showCollectorError">
          <strong>Have questions?</strong>
          <br>
          <span>Check out our <a :href="brandingService.getSupportUrl(csvImports)" target="_blank">knowledge base article</a> containing the details, examples and FAQs about importing and updating data.</span>
        </div>
      </div>
    </vyModal>
  </section>
</template>
<script>
import Modal from '../../../../vueComponents/modal/src/modal.vue';
import startingGuide from './StartingGuide.vue';
import BrandingService from '../../../../services/branding.service';
import BuildingsService from '../../services/BuildingsService';
import SensorsService from '../../services/SensorsService';
import FileService from '../../services/FileService';
import NetworksService from '../../services/NetworksService';
import SpacesService from '../../services/SpacesService';
import BuildingsGuidelines from './BuildingsGuidelines';
import NetworksGuidelines from './NetworksGuidelines';
import SpacesGuidelines from './SpacesGuidelines';
import SensorsGuidelines from './SensorsGuidelines';

const SERVICE_MAP = {
  buildings: BuildingsService,
  networks: NetworksService,
  spaces: SpacesService,
  sensors: SensorsService,
}

export default {
  name: 'csvActionsModal',
  props: {
    datasetName: {
        type: String,
        required: true
    },
    showUploadDownloadModal: {
        type: Boolean,
        default: false
      },
  },
  components: {
    BuildingsGuidelines,
    NetworksGuidelines,
    SpacesGuidelines,
    SensorsGuidelines,
    'vyModal': Modal,
    startingGuide,
  },
  data: function() {
    return {
        brandingService: BrandingService,
        showUploadModal: false,
        showDownloadModal: false,
        showSetupSteps: false,
        showCSVError: false,
        showCollectorError: false,
        showUploadStarted: false,
        showUploadSuccessful: false,
        uploadError: null,
        uploadResponse: null,
    };
  },
  methods: {
    upload: async function(event) {
      let self = this;
      let currentService = SERVICE_MAP[this.datasetName];
      if (event) {
        event.stopImmediatePropagation();
      }
          this.$emit('close');
      try {
        let file = await FileService.selectFile();
        self.setUploadModalBody('uploadStarted');
        // we need setTimeout to give Vue enough time to render after changing the showStepsModal flag
        setTimeout(() => { self.showUploadModal = true; }, 1);
        try {
          let uploadResponse = await currentService.upload(file);
          let requestId = uploadResponse.data.requestId;
          let uploadStatus = await currentService.pollUploadProgress(requestId);
          self.setUploadModalBody('uploadSuccess', uploadStatus);
          self.reloadData++;
        } catch (err) {
          if (err.response) {
            self.setUploadModalBody('csvError', err.response.data.message);
          } else {
            self.setUploadModalBody('csvError', err);
          }
        }
      } catch (err) {
        // user clicked "cancel" on the file dialog, so we do nothing
      }
    },
    downloadTemplate: async function(event) {
      let currentService = SERVICE_MAP[this.datasetName];
      if (event) {
        event.stopImmediatePropagation();
      }
      this.$emit('close')
      currentService.downloadTemplate();
      this.showDownloadModal = true;
    },
    // messageKey must be one of 'uploadStarted', 'uploadSuccess', 'csvError', 'collectorError'
    setUploadModalBody: function(messageKey, response) {
      let self = this;
      switch (messageKey) {
        case 'uploadStarted':
          self.showUploadStarted = true;
          self.showUploadSuccessful = false;
          self.showCSVError = false;
          self.showCollectorError = false;
          break;
        case 'uploadSuccess':
          self.showUploadStarted = false;
          self.showUploadSuccessful = true;
          self.showCSVError = false;
          self.uploadResponse = response;
          self.showCollectorError = false;
          break;
        case 'csvError':
          self.showUploadStarted = false;
          self.showUploadSuccessful = false;
          self.showCSVError = true;
          self.uploadError = response;
          self.showCollectorError = false;
          break;
        case 'collectorError':
          console.log('collector error???');
          self.showUploadStarted = false;
          self.showUploadSuccessful = false;
          self.showCSVError = false;
          self.showCollectorError = true;
          break;
        default: return;
      }
    },
  }
};
</script>
<style lang="scss">
.csvActionsModal {
  .upload-download-modal {
    .upload-download-body {
      display: flex;
      flex-direction: row;
      justify-content: space-evenly;
      .upload, .download {
        border: 2px solid #d5d5d5;
        display: inline-block;
        width: 350px;
        padding: 25px;
        .divider {
          margin: 10px 0;
          border-top: 3px solid #d5d5d5;
        }
        .upload-button {
          color: white;
        }
        .centered {
          text-align: center;
          font-size: 10px;
          margin: 10px;
        }
        header {
          display: flex;
          justify-content: space-between;
          h1 {
            display: inline-block;
            margin: 0;
            letter-spacing: unset;
          }
          .upload-icon, .download-icon {
            display: inline-block;
            margin-top: 5px;
          }
        }
      }
    }
  }

  .modal-mask .modal-wrapper .modal-container {
    max-height: 90vh;
    overflow: hidden;

    .modal-subheader {
      font-weight: bold;
      font-size: 16px;;
    }

    .modal-header div {
      position: relative;
      h1 {
        margin: 0;
        font-size: 18px;
        font-weight: bold;
        line-height: 35px;
      }
      .text-xs-right {
        position: absolute;
        right: -5px;
        top: -10px;

        .close-icon {
          cursor: pointer;
        }
      }
    }

    .modal-footer {
      justify-content: center;
      text-align: center;
      font-size: 12px;
    }
  }
}
</style>